// pages/DashboardPage.jsx
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNotification } from "../context/NotificationContext";
import { StatCard, Card, OccupancyChart } from "../components/UI";
import * as api from "../services/dynamicPriceApi";

const companyId = localStorage.getItem("companyid") || 1;

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const fmt = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `₹${Math.round(n).toLocaleString("en-IN")}`;
  }
};

const safeNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

// Format last_run.started_at → "Today 06:00" or "23 Feb 06:00"
function formatRunTime(isoString) {
  if (!isoString) return "Never";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "Never";

  const today = new Date();
  const isToday =
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear();

  const time = d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  if (isToday) return `Today ${time}`;

  return (
    d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }) + " " + time
  );
}

const SRC_COLORS = {
  PMS: { color: "#6d28d9", bg: "#f5f3ff", border: "#c4b5fd" },
  BE: { color: "#0369a1", bg: "#e0f2fe", border: "#7dd3fc" },
  OTA: { color: "#be185d", bg: "#fdf2f8", border: "#f9a8d4" },
};

// Try to normalize trend data if backend starts returning it later
function normalizeOccupancyTrend(input) {
  if (!Array.isArray(input)) return [];

  return input
    .map((x, idx) => {
      const occ =
        safeNum(x?.occ, null) ??
        safeNum(x?.occupancy, null) ??
        safeNum(x?.occupancy_pct, null);

      if (occ == null) return null;

      const dayLabel =
        x?.day ||
        x?.label ||
        (x?.date
          ? new Date(`${x.date}T00:00:00`).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
            })
          : `D${idx + 1}`);

      return { day: dayLabel, occ };
    })
    .filter(Boolean);
}

// Skeleton block
function SkeletonBox({ height = 80, radius = 12 }) {
  return (
    <div
      style={{
        height,
        borderRadius: radius,
        background:
          "linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 37%, #f1f5f9 63%)",
        backgroundSize: "400% 100%",
        animation: "dpShimmer 1.4s ease infinite",
      }}
    />
  );
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────
export default function DashboardPage({ onNav }) {
  const { notify } = useNotification();

  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState(null);          // /dashboard
  const [report, setReport] = useState(null);        // /reports/monthly
  const [competitors, setCompetitors] = useState([]);// /competitors
  const [loadError, setLoadError] = useState("");

  // Optional trend endpoint (if/when added). Kept as local state, no mock fallback.
  const [occupancyTrend, setOccupancyTrend] = useState([]);
  const [trendAvailable, setTrendAvailable] = useState(false);

  const currentMonth = useMemo(() => new Date().toISOString().slice(0, 7), []);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setLoadError("");

    try {
      const [statsRes, reportRes, compRes] = await Promise.all([
        api.getDashboardStats(companyId),
        api.getMonthlyReport(companyId, currentMonth),
        api.getCompetitors(companyId),
      ]);

      const statsData = statsRes?.data?.data ?? statsRes?.data ?? {};
      const reportData = reportRes?.data?.data ?? reportRes?.data ?? {};
      const compData = compRes?.data?.data ?? compRes?.data ?? [];

      setStats(statsData || {});
      setReport(reportData || {});
      setCompetitors(Array.isArray(compData) ? compData : []);

      // Optional: if backend later includes occupancy trend in dashboard payload
      const trend =
        normalizeOccupancyTrend(statsData?.occupancy_trend) ||
        normalizeOccupancyTrend(reportData?.occupancy_trend) ||
        [];

      if (trend.length > 0) {
        setOccupancyTrend(trend);
        setTrendAvailable(true);
      } else {
        setOccupancyTrend([]);
        setTrendAvailable(false);
      }
    } catch (err) {
      console.error("Dashboard load error:", err);
      setLoadError(err?.response?.data?.message || "Failed to load dashboard.");
      notify("error", err?.response?.data?.message || "Failed to load dashboard. Please refresh.");
    } finally {
      setLoading(false);
    }
  }, [notify, currentMonth]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const pending = safeNum(stats?.pending);
  const approved = safeNum(stats?.approved);
  const modified = safeNum(stats?.modified);

  const handleRun = async () => {
    setRunning(true);
    try {
      const res = await api.runAnalysis(companyId);
      const generated =
        res?.data?.summary?.total_generated ??
        res?.data?.data?.total_generated ??
        res?.data?.total_generated ??
        0;

      notify("success", `Analysis complete — ${generated} recommendations generated.`);
      await loadDashboard();
    } catch (err) {
      notify("error", err?.response?.data?.message || "Analysis failed. Please try again.");
    } finally {
      setRunning(false);
    }
  };

  // ── System Status ──────────────────────────────────────────
  const lastRun = stats?.last_run || null;
  const lastRunTime = formatRunTime(lastRun?.started_at);

  const lastStatusRaw = String(lastRun?.status || "").toLowerCase();
  const cronOk = ["success", "completed", "ok"].includes(lastStatusRaw);

  const scraperErrors = safeNum(stats?.competitor_errors, 0);
  const competitorActive = safeNum(stats?.competitor_active, 0);

  const systemStatus = [
    {
      label: "PMS Connection",
      value: stats?.pms_connected === false ? "Disconnected" : "Connected",
      ok: stats?.pms_connected === false ? false : true,
    },
    {
      label: "Last AI Run",
      value: lastRun
        ? `${lastRunTime} · ${safeNum(lastRun?.recs_generated, 0)} recs`
        : "Never run",
      ok: !!lastRun && cronOk,
    },
    {
      label: "Last Run Status",
      value: lastRun ? (cronOk ? "Success" : lastRun?.status || "Failed") : "—",
      ok: !!lastRun && cronOk,
    },
    {
      label: "Competitor Scraper",
      value:
        scraperErrors === 0
          ? "All OK"
          : `${scraperErrors} error${scraperErrors > 1 ? "s" : ""}`,
      ok: scraperErrors === 0,
    },
  ];

  const activeCompetitors = competitors.filter(
    (c) => String(c?.status || "").toLowerCase() === "active"
  );

  // Monthly report normalized
  const upliftPct = safeNum(report?.uplift_pct, 0);
  const upliftAmount = safeNum(report?.uplift_amount, 0);
  const aiRevenue = safeNum(report?.ai_revenue, 0);
  const baseRevenue = safeNum(report?.base_revenue, 0);
  const bySource = Array.isArray(report?.by_source) ? report.by_source : [];

  // ── Loading state ──────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: "28px 32px" }}>
        <style>{`
          @keyframes dpShimmer {
            0% { background-position: 100% 0; }
            100% { background-position: -100% 0; }
          }
        `}</style>

        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24, gap: 16 }}>
          <div style={{ flex: 1 }}>
            <SkeletonBox height={28} radius={8} />
            <div style={{ height: 8 }} />
            <SkeletonBox height={16} radius={8} />
          </div>
          <div style={{ width: 220 }}>
            <SkeletonBox height={40} radius={10} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(160px,1fr))", gap: 12, marginBottom: 18 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonBox key={i} height={92} radius={14} />
          ))}
        </div>

        <div className="row g-3">
          <div className="col-xl-6"><SkeletonBox height={320} radius={14} /></div>
          <div className="col-xl-6"><SkeletonBox height={320} radius={14} /></div>
          <div className="col-xl-6"><SkeletonBox height={280} radius={14} /></div>
          <div className="col-xl-6"><SkeletonBox height={280} radius={14} /></div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "28px 32px" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h4
            style={{
              fontFamily: "'Playfair Display',serif",
              fontSize: 25,
              color: "#0f172a",
              margin: 0,
              fontWeight: 700,
            }}
          >
            Good morning, Revenue Manager 👋
          </h4>
          <p
            style={{
              margin: "5px 0 0",
              fontSize: 13,
              color: "#64748b",
              fontWeight: 500,
            }}
          >
            {report?.hotel_name || stats?.hotel_name || "Dashboard"} &nbsp;·&nbsp;
            {new Date().toLocaleDateString("en-IN", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="dp-btn dp-btn-outline"
            onClick={() => onNav?.("recommendations")}
          >
            Review {pending} Pending →
          </button>
          <button
            className="dp-btn dp-btn-primary"
            onClick={handleRun}
            disabled={running}
          >
            {running ? (
              <>
                <span className="dp-spin">⟳</span> Analysing…
              </>
            ) : (
              "⚡ Run AI Analysis"
            )}
          </button>
        </div>
      </div>

      {/* Load error banner */}
      {!!loadError && (
        <div
          style={{
            marginBottom: 16,
            padding: "10px 14px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 10,
            color: "#991b1b",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {loadError}
        </div>
      )}

      {/* KPI Row */}
      <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
        <StatCard
          label="Pending Review"
          value={pending}
          sub="Awaiting your action"
          color="#f59e0b"
          icon="⏳"
          onClick={() => onNav?.("recommendations")}
        />
        <StatCard
          label="Approved"
          value={approved}
          sub="AI prices live"
          color="#16a34a"
          icon="✓"
        />
        <StatCard
          label="Modified"
          value={modified}
          sub="Custom price set"
          color="#2563eb"
          icon="✏"
        />
        <StatCard
          label="Monthly Uplift"
          value={report ? `${upliftPct >= 0 ? "+" : ""}${upliftPct}%` : "—"}
          sub="vs base revenue"
          color="#8b5cf6"
          icon="📈"
        />
        <StatCard
          label="Monthly Impact"
          value={report ? fmt(upliftAmount) : "—"}
          sub={report ? `${upliftPct >= 0 ? "+" : ""}${upliftPct}% vs base` : "No report yet"}
          color="#ec4899"
          icon="💰"
          onClick={() => onNav?.("reports")}
        />
      </div>

      {/* Row 2: Charts */}
      <div className="row g-3 mb-3">
        {/* Occupancy Trend (API-only; empty state if not provided) */}
        <div className="col-xl-6">
          <Card>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20,
              }}
            >
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                  Occupancy Trend
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  {trendAvailable
                    ? "Next days occupancy from live data"
                    : "Occupancy trend endpoint not configured yet"}
                </div>
              </div>
              <span
                style={{
                  fontSize: 11,
                  background: trendAvailable ? "#f0fdf4" : "#f8fafc",
                  color: trendAvailable ? "#16a34a" : "#64748b",
                  border: `1px solid ${trendAvailable ? "#86efac" : "#e2e8f0"}`,
                  borderRadius: 20,
                  padding: "3px 10px",
                  fontWeight: 700,
                }}
              >
                {trendAvailable ? "● LIVE" : "○ NOT CONFIGURED"}
              </span>
            </div>

            {trendAvailable && occupancyTrend.length > 0 ? (
              <OccupancyChart data={occupancyTrend} />
            ) : (
              <div
                style={{
                  minHeight: 220,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px dashed #cbd5e1",
                  borderRadius: 12,
                  background: "#f8fafc",
                  textAlign: "center",
                  padding: 20,
                }}
              >
                <div>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>📉</div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#334155",
                      marginBottom: 4,
                    }}
                  >
                    Occupancy trend unavailable
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    Add backend occupancy trend response in dashboard/report API to show chart.
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Revenue Impact */}
        <div className="col-xl-6">
          <Card>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 2 }}>
              Revenue Impact · {report?.month ?? currentMonth}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 16 }}>
              Approved + modified AI recommendations
            </div>

            <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
              {[
                { label: "AI Revenue", value: fmt(aiRevenue), accent: "#16a34a" },
                { label: "Without AI", value: fmt(baseRevenue), accent: "#94a3b8" },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    flex: 1,
                    minWidth: 180,
                    background: "#f8fafc",
                    borderRadius: 10,
                    padding: "12px 16px",
                    borderLeft: `3px solid ${item.accent}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      marginBottom: 5,
                      fontWeight: 600,
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    style={{
                      fontFamily: "'DM Mono',monospace",
                      fontSize: 18,
                      fontWeight: 800,
                      color: "#0f172a",
                    }}
                  >
                    {item.value}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                background:
                  upliftAmount >= 0
                    ? "linear-gradient(135deg,#f0fdf4,#dcfce7)"
                    : "linear-gradient(135deg,#fff1f2,#ffe4e6)",
                borderRadius: 10,
                padding: "14px 18px",
                border: `1px solid ${upliftAmount >= 0 ? "#86efac" : "#fda4af"}`,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: upliftAmount >= 0 ? "#15803d" : "#be123c",
                  fontWeight: 700,
                  marginBottom: 3,
                }}
              >
                AI Revenue Uplift This Month
              </div>
              <div
                style={{
                  fontFamily: "'DM Mono',monospace",
                  fontSize: 22,
                  fontWeight: 800,
                  color: upliftAmount >= 0 ? "#15803d" : "#be123c",
                }}
              >
                {`${upliftAmount >= 0 ? "+" : ""}${fmt(upliftAmount)}`}
                <span style={{ fontSize: 14, marginLeft: 6, fontWeight: 600 }}>
                  ({upliftPct >= 0 ? "+" : ""}
                  {upliftPct}%)
                </span>
              </div>
            </div>

            {bySource.map((s, idx) => {
              const source = s?.source || "PMS";
              const cc = SRC_COLORS[source] || SRC_COLORS.PMS;
              return (
                <div
                  key={`${source}-${idx}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "9px 0",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        color: cc.color,
                        background: cc.bg,
                        border: `1.5px solid ${cc.border}`,
                        borderRadius: 5,
                        padding: "2px 8px",
                      }}
                    >
                      {source}
                    </span>
                    <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>
                      {safeNum(s?.generated)} recommendations
                    </span>
                  </div>
                  <span
                    style={{
                      fontFamily: "'DM Mono',monospace",
                      fontSize: 13,
                      color: safeNum(s?.uplift_pct) >= 0 ? "#15803d" : "#be123c",
                      fontWeight: 700,
                    }}
                  >
                    {safeNum(s?.uplift_pct) >= 0 ? "+" : ""}
                    {safeNum(s?.uplift_pct)}%
                  </span>
                </div>
              );
            })}

            {report && bySource.length === 0 && (
              <div
                style={{
                  fontSize: 12,
                  color: "#94a3b8",
                  textAlign: "center",
                  padding: "16px 0",
                }}
              >
                No approved recommendations this month yet.
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Row 3: Competitors + System Status */}
      <div className="row g-3">
        {/* Competitor Prices */}
        <div className="col-xl-6">
          <Card>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                Competitor Prices
              </div>
              <button
                className="dp-btn dp-btn-ghost dp-btn-sm"
                onClick={() => onNav?.("competitors")}
              >
                Manage →
              </button>
            </div>

            {activeCompetitors.length === 0 && (
              <div style={{ fontSize: 12, color: "#94a3b8", padding: "12px 0" }}>
                No active competitors tracked.{" "}
                <button
                  className="dp-btn dp-btn-ghost dp-btn-sm"
                  onClick={() => onNav?.("competitors")}
                >
                  Add one →
                </button>
              </div>
            )}

            {activeCompetitors.map((c) => {
              const deluxePrice = c?.prices?.Deluxe ?? c?.prices?.deluxe ?? null;
              const scraped = c?.last_scraped_at
                ? new Date(c.last_scraped_at).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "Never";

              return (
                <div
                  key={c.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 0",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <div style={{ minWidth: 0, paddingRight: 10 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#0f172a",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={c?.name}
                    >
                      {c?.name || "Unnamed Competitor"}
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                      {c?.platform || "Platform"} · {scraped}
                    </div>
                  </div>

                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div
                      style={{
                        fontFamily: "'DM Mono',monospace",
                        fontSize: 14,
                        color: "#d97706",
                        fontWeight: 700,
                      }}
                    >
                      {deluxePrice != null ? fmt(deluxePrice) : "—"}
                    </div>
                    <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>
                      Deluxe / night
                    </div>
                  </div>
                </div>
              );
            })}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 10,
                padding: "8px 12px",
                background: "#f8fafc",
                borderRadius: 8,
              }}
            >
              <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>
                {competitorActive} active · {scraperErrors} errors
              </span>
              <span
                style={{
                  fontSize: 11,
                  background: scraperErrors === 0 ? "#f0fdf4" : "#fffbeb",
                  color: scraperErrors === 0 ? "#16a34a" : "#b45309",
                  border: `1px solid ${scraperErrors === 0 ? "#86efac" : "#fde68a"}`,
                  borderRadius: 20,
                  padding: "2px 8px",
                  fontWeight: 700,
                }}
              >
                {scraperErrors === 0 ? "● Live" : "⚠ Needs Attention"}
              </span>
            </div>
          </Card>
        </div>

        {/* System Status */}
        <div className="col-xl-6">
          <Card>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                System Status
              </div>
              <button
                className="dp-btn dp-btn-ghost dp-btn-sm"
                onClick={() => onNav?.("integration")}
              >
                Configure →
              </button>
            </div>

            {systemStatus.map((item) => (
              <div
                key={item.label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "9px 0",
                  borderBottom: "1px solid #f1f5f9",
                  gap: 10,
                }}
              >
                <span style={{ fontSize: 13, color: "#475569", fontWeight: 500 }}>
                  {item.label}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: item.ok ? "#15803d" : "#d97706",
                    fontFamily: "'DM Mono',monospace",
                    textAlign: "right",
                  }}
                >
                  {item.ok ? "● " : "⚠ "}
                  {item.value}
                </span>
              </div>
            ))}

            {/* Last run error detail */}
            {lastRun && !cronOk && lastRun?.error_message && (
              <div
                style={{
                  marginTop: 10,
                  padding: "10px 14px",
                  background: "#fef2f2",
                  border: "1px solid #fca5a5",
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: "#b91c1c",
                    fontWeight: 700,
                    marginBottom: 2,
                  }}
                >
                  ✕ Last AI run failed
                </div>
                <div style={{ fontSize: 11, color: "#991b1b" }}>
                  {lastRun.error_message}
                </div>
              </div>
            )}

            {/* Competitor scraper error */}
            {scraperErrors > 0 && (
              <div
                style={{
                  marginTop: 10,
                  padding: "10px 14px",
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: "#b45309",
                    fontWeight: 700,
                    marginBottom: 2,
                  }}
                >
                  ⚠ {scraperErrors} competitor scraper{scraperErrors > 1 ? "s" : ""} failed
                </div>
                <div style={{ fontSize: 11, color: "#92400e" }}>
                  Some competitor prices may be stale.{" "}
                  <button
                    onClick={() => onNav?.("competitors")}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#b45309",
                      cursor: "pointer",
                      textDecoration: "underline",
                      fontSize: 11,
                      fontWeight: 600,
                      padding: 0,
                    }}
                  >
                    Review competitors →
                  </button>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}